import { useEffect, useMemo, useState } from 'react';
import { Terrain } from '../composants/Terrain.jsx';
import { FeuilleChoix } from '../composants/FeuilleChoix.jsx';
import { FicheJoueur } from '../composants/FicheJoueur.jsx';
import { BandeauMatch } from '../composants/BandeauMatch.jsx';
import { Bouton, Chip, MONO, toucher } from '../composants/base.jsx';
import { ordreChips, PHASE_DEFENSIVE } from '../data/formations.js';
import { STATUTS } from '../data/postes.js';
import { placer, echanger, retirer, placesEnPhase, banc, formationDePhase, estComplete, viderCompo } from '../logique/compo.js';
import { parJoueur, minutesEntree, jaunesParJoueur, rougesParJoueur, ajouter, retirerEvenement, compteurs, score, LIBELLES } from '../logique/match.js';
import { Feuille } from '../composants/Feuille.jsx';
import { minutes as minutesDe } from '../local/chrono.js';
import { Soleil, Lune, Inverser, Ballon, Bouclier, Rafraichir, Vers } from '../composants/icones.jsx';

export function EcranTerrain({
  instantane, compo, match, chrono, theme, fraicheur,
  onCompo, onMatch, onChangement, onChrono, onMiTemps, onTheme, onFormation,
  onReprefiller, onConfirmer, onCoupDEnvoi, onFinDuMatch, onNouveauMatch,
}) {
  const [retourne, setRetourne] = useState(false);
  const [feuille, setFeuille] = useState(null);      // {genre:'choix'|'fiche', index, joueurId}
  const [deplace, setDeplace] = useState(null);      // index du joueur qu'on déplace
  const [sortant, setSortant] = useState(null);      // index du joueur qui sort (changement)

  const effectif = instantane?.effectif ?? [];
  const parId = useMemo(() => new Map(effectif.map((j) => [j.id, j])), [effectif]);
  const jaunes = useMemo(() => jaunesParJoueur(match), [match]);
  const rouges = useMemo(() => rougesParJoueur(match), [match]);

  // Les cartons du match en cours se greffent sur la fiche du joueur : le liseré du
  // jeton et la fiche doivent dire la même chose.
  const effectifVivant = useMemo(
    () => effectif.map((j) => (jaunes[j.id] || rouges[j.id] ? { ...j, jaunes: jaunes[j.id] ?? 0, rouges: rouges[j.id] ?? 0 } : j)),
    [effectif, jaunes, rouges]
  );

  const places = placesEnPhase(compo);
  const surLeBanc = useMemo(() => banc(compo, effectifVivant), [compo, effectifVivant]);
  const absents = instantane?.absents ?? [];
  const c = compteurs(match);
  const formationAffichee = formationDePhase(compo.formation, compo.phase);

  // L'ÉTAT VIENT DE LA COMPO, ET D'ELLE SEULE.
  //
  // Il se déduisait aussi du chrono : « un chrono qui tourne l'emporte ». C'était un
  // second chemin vers l'état « en cours », et il court-circuitait le premier — on
  // démarrait le chrono depuis le bandeau, l'écran affichait un match en cours, mais
  // la compo restait un brouillon et RIEN n'était enregistré. Un seul chemin, une
  // seule vérité : on avance par les boutons du bas, jamais autrement.
  const onzePoses = compo.places.filter((p) => p.joueurId).length;
  const complete = estComplete(compo);
  const etat = compo.etat ?? 'probable';

  const slotDe = (i) => compo.places[i];
  const joueurDe = (i) => parId.get(compo.places[i]?.joueurId);

  const minuteCourante = () => (chrono.etat === 'arrete' ? 0 : minutesDe(chrono));

  const ficheJoueur = feuille?.genre === 'fiche'
    ? (feuille.index != null ? joueurDe(feuille.index) : parId.get(feuille.joueurId))
    : null;

  // Ajouter OU retirer. Le retrait enlève le dernier événement de ce type pour ce
  // joueur : se tromper de nom à la 70e arrive, et il faut pouvoir défaire sans
  // ouvrir le journal.
  // Saisissable dès que le onze est figé. N'ouvrir qu'à « en cours » créait une
  // impasse : on touchait un joueur pendant le match et aucune tuile n'apparaissait,
  // parce que le chrono n'avait pas été lancé — ce qui n'a rien à voir avec le fait
  // qu'un but ait été marqué.
  function evenementJoueur(type, delta = 1) {
    const j = ficheJoueur;
    if (!j) return;
    toucher(12);
    if (delta > 0) {
      onMatch(ajouter(match, { type, joueurId: j.id, minute: minuteCourante() }));
      return;
    }
    const dernier = [...match.evenements].reverse().find((e) => e.type === type && e.joueurId === j.id);
    if (dernier) onMatch(retirerEvenement(match, dernier.id));
  }

  // Les compteurs du joueur ouvert, pour que la fiche montre l'état au lieu d'empiler.
  const compteursJoueur = useMemo(() => {
    const id = ficheJoueur?.id;
    if (!id) return {};
    const c = {};
    for (const e of match.evenements) if (e.joueurId === id) c[e.type] = (c[e.type] ?? 0) + 1;
    return c;
  }, [match, ficheJoueur?.id]);

  function tapPlace(i) {
    toucher();

    // Un changement en cours : le premier toucher désigne le sortant, le second se
    // fait dans le banc. Deux touchers, et rien à confirmer.
    if (sortant === null && feuille?.genre === 'changement') {
      if (!compo.places[i].joueurId) return;
      setSortant(i);
      return;
    }

    if (deplace !== null) {
      if (deplace !== i) onCompo(echanger(compo, deplace, i));
      setDeplace(null);
      return;
    }

    if (!compo.places[i].joueurId) setFeuille({ genre: 'choix', index: i });
    else setFeuille({ genre: 'fiche', index: i });
  }

  function choisir(joueur) {
    onCompo(placer(compo, feuille.index, joueur.id));
    setFeuille(null);
  }

  function tapBanc(j) {
    toucher();
    if (sortant !== null) {
      const sortId = compo.places[sortant].joueurId;
      // Une seule opération : le journal et le terrain changent ensemble, et sont
      // enregistrés ensemble. Deux appels séparés laissaient le sortant sur le onze
      // archivé, parce que le premier partait avec l'état d'avant le second.
      onChangement(
        {
          id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
          type: 'chg', sortId, entreId: j.id,
          minute: minuteCourante(),
          miTemps: chrono.etat === 'pause' && chrono.periode === 1,
        },
        placer(compo, sortant, j.id).places
      );
      setSortant(null);
      setFeuille(null);
      return;
    }
    setFeuille({ genre: 'fiche', joueurId: j.id });
  }

  const enChangement = feuille?.genre === 'changement';
  const prochain = instantane?.prochain;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {/* L'en-tête tient sur une ligne. La fraîcheur et le nombre d'absents y sont
          fondus : ils occupaient une bannière à eux seuls, soit quarante pixels pris
          au terrain pour deux informations qui tiennent en une demi-ligne. */}
      <header style={{ flex: 'none', padding: 'calc(env(safe-area-inset-top) + 6px) 14px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, flex: 'none', borderRadius: 9, background: 'var(--acc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11.5, color: 'var(--tokink)' }}>AJA</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {titreMatch(compo, prochain)}
            </div>
            {/* L'ÉTAT EN PREMIER, avant la fraîcheur des données.
                Modifier un joueur puis aller dans Mémoire sans rien y trouver donne
                l'impression d'avoir perdu son travail : rien ne disait que tant qu'on
                n'a pas touché « C'est la compo », il n'y a pas de match, seulement un
                brouillon. Le brouillon survit aux relancements, mais il n'entre ni
                dans l'historique ni dans les statistiques — et il faut le dire. */}
            <div style={{ fontSize: 10, fontFamily: MONO, color: 'var(--ink2)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span style={{ color: etatCouleur(etat), fontWeight: 700 }}>{ETAT_LIBELLE[etat]}</span>
              {' · '}{fraicheur}{absents.length ? ` · ${absents.length} absent${absents.length > 1 ? 's' : ''}` : ''}
            </div>
          </div>
          <BoutonIcone onClick={onTheme} aria-label={theme === 'sombre' ? 'Passer au thème clair' : 'Passer au thème sombre'}>
            {theme === 'sombre' ? <Soleil /> : <Lune />}
          </BoutonIcone>
          <BoutonIcone onClick={() => setRetourne((r) => !r)} aria-label="Inverser le sens du terrain"><Inverser /></BoutonIcone>
          {/* Les actions rares vivent ici, pas dans le tiers du pouce. Vider onze
              jetons est destructeur et sert une fois par saison ; lui donner en
              permanence la moitié de la barre du bas, c'est dépenser la meilleure
              place de l'écran pour ce qu'on ne touche presque jamais. */}
          <BoutonIcone onClick={() => setFeuille({ genre: 'menu' })} aria-label="Autres actions">⋯</BoutonIcone>
        </div>
      </header>

      <div className="defileX" style={{ flex: 'none', display: 'flex', gap: 6, padding: '0 14px 8px' }}>
        {ordreChips(instantane?.formationRecente).map((f) => (
          <Chip key={f} actif={compo.formation === f} onClick={() => { toucher(); onFormation(f); }}>{f}</Chip>
        ))}
        {absents.length > 0 && (
          <Chip
            onClick={() => setFeuille({ genre: 'absents' })}
            style={{ fontFamily: 'var(--police)', fontSize: 12, color: 'var(--warn)', borderColor: 'var(--line)' }}
          >
            {absents.length} absents
          </Chip>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, margin: '0 12px', display: 'flex' }}>
        <Terrain
          style={{ flex: 1 }}
          places={places}
          effectif={effectifVivant}
          retourne={retourne}
          selection={deplace ?? sortant}
          cibles={deplace !== null ? compo.places.map((p) => p.index).filter((i) => i !== deplace) : []}
          evenements={parJoueur(match)}
          entrees={minutesEntree(match)}
          onTapPlace={tapPlace}
        >
          {/* Les commandes vivent SUR le terrain, qui a de la place en largeur et n'en
              a aucune en hauteur. Empilées au-dessus et au-dessous, elles laissaient au
              terrain moins de hauteur que de largeur : un terrain de football en
              paysage sur un téléphone tenu debout. */}
          <div style={{ position: 'absolute', left: 8, top: 8, display: 'flex', padding: 3, borderRadius: 11, background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(6px)' }}>
            <Phase actif={compo.phase === 'offensive'} onClick={() => onCompo({ ...compo, phase: 'offensive' })}><Ballon t={14} /> Avec</Phase>
            <Phase actif={compo.phase === 'defensive'} onClick={() => onCompo({ ...compo, phase: 'defensive' })}><Bouclier t={14} /> Sans</Phase>
          </div>
          <div style={{ position: 'absolute', right: 8, top: 8, padding: '6px 9px', borderRadius: 9, background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(6px)', fontFamily: MONO, fontWeight: 700, fontSize: 11, color: '#fff' }}>
            {formationAffichee}
          </div>
        </Terrain>
      </div>

      {/* Le bandeau n'est plus une porte d'entrée, c'est un tableau de bord : il ne
          paraît qu'une fois le match lancé. Tant qu'il portait le bouton de départ, il
          offrait un second chemin vers l'état « en cours » — et ce chemin sautait
          l'enregistrement. */}
      {(etat === 'en-cours' || etat === 'jouee') && (
      <BandeauMatch
        chrono={chrono}
        match={match}
        adversaire={compo.adversaire ?? adversaireDe(prochain, instantane?.equipe?.id)}
        onChrono={onChrono}
        onButAdverse={() => { toucher(12); onMatch(ajouter(match, { type: 'butAdverse', minute: minuteCourante() })); }}
        onJournal={() => setFeuille({ genre: 'journal' })}
      />
      )}

      <div style={{ flex: 'none', padding: '7px 0 2px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 16px 5px' }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.8px', color: sortant !== null ? 'var(--acc2)' : 'var(--ink2)' }}>
            {sortant !== null ? 'QUI ENTRE ?' : `BANC · ${surLeBanc.length}`}
          </span>
          {sortant !== null && <button onClick={() => { setSortant(null); setFeuille(null); }} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink2)' }}>Annuler</button>}
        </div>
        <div className="defileX" style={{ display: 'flex', gap: 7, padding: '0 14px 2px' }}>
          {surLeBanc.map((j) => {
            const st = STATUTS[j.statut];
            const dispo = j.statut === 'ok' || j.statut === 'doute';
            return (
              <button
                key={j.id}
                onClick={() => dispo && tapBanc(j)}
                style={{
                  flex: 'none', display: 'flex', alignItems: 'center', gap: 7,
                  height: 44, padding: '0 11px 0 9px', borderRadius: 12,
                  background: sortant !== null && dispo ? 'var(--surf2)' : 'var(--surf)',
                  border: `1px solid ${sortant !== null && dispo ? 'var(--acc2)' : 'var(--line)'}`,
                  opacity: dispo ? 1 : .45,
                }}
              >
                <span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 13, color: 'var(--ink)' }}>{j.numero ?? '·'}</span>
                <span style={{ textAlign: 'left' }}>
                  <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.2 }}>{j.nom}</span>
                  <span style={{ display: 'block', fontSize: 8.5, fontFamily: MONO, color: 'var(--ink2)', letterSpacing: '.4px', lineHeight: 1.2 }}>{j.poste}</span>
                </span>
                {j.statut !== 'ok' && <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: st.ton === 'attention' ? 'var(--warn)' : 'var(--bad)' }} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* LE TIERS DU POUCE : un bouton principal, et un seul, qui change avec l'état
          de la journée. Six actions disponibles à la 70ᵉ minute, c'est cinq de trop —
          le déroulé complet est dans docs/JOUR-DE-MATCH.md. */}
      <div style={{ flex: 'none', display: 'flex', gap: 8, padding: '7px 14px 8px' }}>
        {etat === 'probable' && (
          <Bouton
            ton="valide" large taille={50}
            disabled={!complete}
            style={complete ? null : { opacity: .45 }}
            onClick={() => { toucher(14); onConfirmer(); }}
          >
            {complete ? "C'est la compo" : `${onzePoses}/11 placés`}
          </Bouton>
        )}

        {etat === 'annoncee' && (
          <>
            <Bouton ton="fantome" taille={50} onClick={() => onCompo({ ...compo, etat: 'probable' })}>Modifier</Bouton>
            <Bouton ton="principal" large taille={50} onClick={() => { toucher(14); onCoupDEnvoi(); }}>Coup d&apos;envoi</Bouton>
          </>
        )}

        {etat === 'en-cours' && (
          <>
            <Bouton ton="fantome" taille={50} onClick={() => setFeuille({ genre: 'fin' })}>Fin du match</Bouton>
            <Bouton
              ton={enChangement ? 'principal' : 'neutre'} large taille={50}
              disabled={c.epuise}
              style={c.epuise ? { opacity: .5 } : null}
              onClick={() => { toucher(); setSortant(null); setFeuille(enChangement ? null : { genre: 'changement' }); }}
            >
              {enChangement ? 'Touche qui sort' : 'Changement'}
            </Bouton>
          </>
        )}

        {/* Un seul bouton : le journal se consulte depuis le bandeau de match et depuis
            Mémoire, un troisième chemin ne faisait qu'ajouter une décision à prendre. */}
        {etat === 'jouee' && (
          <Bouton ton="principal" large taille={50} onClick={() => { toucher(14); setFeuille({ genre: 'nouveau' }); }}>
            Nouveau match
          </Bouton>
        )}
      </div>

      {deplace !== null && (
        <Consigne onAnnuler={() => setDeplace(null)}>Touche la place où l&apos;envoyer</Consigne>
      )}

      <Feuille
        ouverte={feuille?.genre === 'menu'}
        onFermer={() => setFeuille(null)}
        titre="Autres actions"
        sousTitre={ETAT_LIBELLE[etat]}
        hauteurMax="60dvh"
      >
        <div style={{ padding: '0 18px 24px' }}>
          {etat === 'probable' ? (
            <>
              <ActionMenu
                titre="Repartir du onze probable"
                detail="Remet les joueurs que l'entraîneur a alignés au dernier match dans ce système."
                onClick={() => { setFeuille(null); onReprefiller(); }}
              />
              <ActionMenu
                titre="Vider le terrain"
                detail="Retire les onze joueurs et laisse les créneaux ouverts."
                danger
                onClick={() => {
                  if (!confirm('Vider le terrain ? Les onze joueurs posés seront retirés.')) return;
                  setFeuille(null);
                  onCompo(viderCompo(compo));
                }}
              />
            </>
          ) : etat === 'en-cours' ? (
            <>
              <ActionMenu
                titre={chrono.periode === 1 ? 'Mi-temps' : 'Seconde période'}
                detail="Le chrono repart à 45 minutes. La mi-temps ne consomme pas de fenêtre de changement."
                onClick={() => { setFeuille(null); onMiTemps(); }}
              />
              <ActionMenu
                titre="Voir le déroulé"
                detail="Tous les événements à leur minute, et de quoi en retirer un."
                onClick={() => setFeuille({ genre: 'journal' })}
              />
            </>
          ) : (
            <div style={{ padding: '10px 0 16px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--ink2)' }}>
              {etat === 'jouee'
                ? 'Ce match est terminé et archivé. Il sert d\u2019historique au pré-remplissage : on ne le modifie plus. « Nouveau match » en commence un autre.'
                : 'Le onze est figé. Touche « Modifier » en bas pour y revenir, puis retrouve ces actions ici.'}
            </div>
          )}
        </div>
      </Feuille>

      <FeuilleNouveauMatch
        ouverte={feuille?.genre === 'nouveau'}
        propose={prochain?.local ?? null}
        onFermer={() => setFeuille(null)}
        onValider={(infos) => { setFeuille(null); onNouveauMatch(infos); }}
      />

      <Feuille
        ouverte={feuille?.genre === 'fin'}
        onFermer={() => setFeuille(null)}
        titre="Terminer le match ?"
        sousTitre={`${score(match).nous}–${score(match).eux} · ${match.evenements.length} événement${match.evenements.length > 1 ? 's' : ''}`}
      >
        <div style={{ padding: '0 18px 22px' }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.55, color: 'var(--ink2)' }}>
            Le onze et le journal sont archivés, et ce onze servira à pré-remplir la
            journée suivante. Une composition terminée ne se modifie plus.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Bouton ton="fantome" large taille={50} onClick={() => setFeuille(null)}>Pas encore</Bouton>
            <Bouton ton="valide" large taille={50} onClick={() => { setFeuille(null); onFinDuMatch(); }}>Terminer</Bouton>
          </div>
        </div>
      </Feuille>

      <Feuille
        ouverte={feuille?.genre === 'absents'}
        onFermer={() => setFeuille(null)}
        titre="Indisponibles"
        sousTitre={`${absents.length} joueur${absents.length > 1 ? 's' : ''} hors du groupe`}
      >
        <div style={{ padding: '0 18px 22px' }}>
          {absents.map((j) => (
            <div key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: '1px solid var(--surf2)' }}>
              <span style={{ width: 32, fontFamily: MONO, fontWeight: 700, fontSize: 13 }}>{j.numero ?? '·'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{j.nom}</span>
                <span style={{ display: 'block', fontSize: 10.5, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>{j.poste}{j.raison ? ` · ${j.raison}` : ''}</span>
              </span>
              <span style={{ flex: 'none', padding: '4px 8px', borderRadius: 7, fontSize: 10, fontWeight: 700, border: `1px solid ${STATUTS[j.statut] ? (STATUTS[j.statut].ton === 'attention' ? 'var(--warn)' : 'var(--bad)') : 'var(--line)'}`, color: STATUTS[j.statut]?.ton === 'attention' ? 'var(--warn)' : 'var(--bad)' }}>
                {STATUTS[j.statut]?.libelle ?? '?'}
              </span>
            </div>
          ))}
        </div>
      </Feuille>

      <Feuille
        ouverte={feuille?.genre === 'journal'}
        onFermer={() => setFeuille(null)}
        titre="Journal du match"
        sousTitre={`${match.evenements.length} événement${match.evenements.length > 1 ? 's' : ''} · ${c.changements}/5 changements`}
      >
        <div style={{ padding: '0 18px 22px' }}>
          <ScoreOfficiel matchId={prochain?.id} equipeId={instantane?.equipe?.id} scoreLocal={score(match)} />

          {match.evenements.length === 0 && (
            <div style={{ padding: '14px 0', fontSize: 12.5, color: 'var(--ink3)', lineHeight: 1.5 }}>
              Rien encore. Touche un joueur pour lui mettre un but ou un carton.
            </div>
          )}
          {match.evenements.map((e) => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: '1px solid var(--surf2)' }}>
              <span style={{ width: 34, fontFamily: MONO, fontWeight: 700, fontSize: 12 }}>{e.minute}&apos;</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
                {LIBELLES[e.type] ?? e.type}
                {e.type === 'chg'
                  ? <span style={{ color: 'var(--ink2)', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 4 }}> · {nomDe(parId, e.sortId)} <Vers t={13} /> {nomDe(parId, e.entreId)}</span>
                  : e.joueurId ? <span style={{ color: 'var(--ink2)', fontWeight: 400 }}> · {nomDe(parId, e.joueurId)}</span> : null}
              </span>
              {/* Retirer un événement est destructif mais sans gravité, et se tromper de
                  joueur à la 70e arrive : il faut pouvoir défaire sans tout reprendre. */}
              <button onClick={() => onMatch(retirerEvenement(match, e.id))} aria-label="Retirer" style={{ flex: 'none', width: 40, height: 40, borderRadius: 11, color: 'var(--bad)', fontSize: 15, border: '1px solid var(--line)' }}>×</button>
            </div>
          ))}
        </div>
      </Feuille>

      <FeuilleChoix
        ouverte={feuille?.genre === 'choix'}
        slot={feuille?.genre === 'choix' ? slotDe(feuille.index) : null}
        effectif={effectifVivant}
        dejaPlaces={compo.places.map((p) => p.joueurId)}
        onChoisir={choisir}
        onFermer={() => setFeuille(null)}
      />

      <FicheJoueur
        ouverte={feuille?.genre === 'fiche'}
        joueur={ficheJoueur}
        surLeTerrain={feuille?.index != null}
        matchEnCours={etat === 'en-cours' || etat === 'annoncee'}
        matchPrepare={etat === 'annoncee'}
        compteurs={compteursJoueur}
        // Le raccourci depuis la fiche : figer le onze, ou donner le coup d'envoi,
        // sans avoir à refermer et à chercher le bouton du bas.
        onDemarrer={() => {
          toucher(14);
          setFeuille(null);
          if (etat === 'annoncee') onCoupDEnvoi(); else if (complete) onConfirmer();
          else setFeuille({ genre: 'choix', index: compo.places.findIndex((p) => !p.joueurId) });
        }}
        onEvenement={evenementJoueur}
        onDeplacer={() => { setDeplace(feuille.index); setFeuille(null); }}
        onRemplacer={() => setFeuille({ genre: 'choix', index: feuille.index })}
        onRetirer={() => { onCompo(retirer(compo, feuille.index)); setFeuille(null); }}
        onFermer={() => setFeuille(null)}
      />
    </div>
  );
}

/**
 * Qui joue, et où.
 *
 * Demandé à la création plutôt que déduit du calendrier : le plan gratuit n'en donne
 * pas, et un match nommé « Match sans adversaire » dans l'historique ne rappelle rien
 * à personne trois journées plus tard.
 */
function FeuilleNouveauMatch({ ouverte, propose, onFermer, onValider }) {
  const [adversaire, setAdversaire] = useState('');
  const [domicile, setDomicile] = useState(true);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Pré-rempli avec la prochaine journée du calendrier : connaissant la date du jour
  // et celle du match, il n'y a plus rien à saisir la plupart du temps. On peut
  // toujours corriger — un amical, une coupe, un report.
  useEffect(() => {
    if (!ouverte) return;
    setAdversaire(propose?.adversaire ?? '');
    setDomicile(propose?.domicile !== false);
    setDate(propose?.date ?? new Date().toISOString().slice(0, 10));
  }, [ouverte, propose]);

  return (
    <Feuille
      ouverte={ouverte}
      onFermer={onFermer}
      titre="Nouveau match"
      sousTitre={propose ? `Journée ${propose.journee} proposée` : 'Qui, et où'}
      hauteurMax="70dvh"
    >
      <div style={{ padding: '0 18px 24px' }}>
        {/* Le raccourci d'un seul toucher quand la proposition est la bonne — c'est
            le cas trente-quatre fois sur trente-quatre. Les champs restent en dessous
            pour les amicaux, les coupes et les reports. */}
        {propose && (
          <button
            onClick={() => onValider({ adversaire: propose.adversaire, domicile: propose.domicile, date: propose.date })}
            style={{
              display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
              padding: '13px 14px', marginBottom: 16, borderRadius: 14,
              background: 'var(--acc)', color: 'var(--tokink)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 15, fontWeight: 800 }}>
                {propose.domicile ? `AJA — ${propose.adversaire}` : `${propose.adversaire} — AJA`}
              </span>
              <span style={{ display: 'block', fontSize: 10.5, fontFamily: MONO, opacity: .75, marginTop: 2 }}>
                {dateLisible(propose.date)} · {propose.domicile ? 'domicile' : 'extérieur'}
              </span>
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, opacity: .8 }}>Commencer</span>
          </button>
        )}

        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '.8px', color: 'var(--ink2)', marginBottom: 6 }}>ADVERSAIRE</span>
          <input
            value={adversaire}
            onChange={(e) => setAdversaire(e.target.value)}
            placeholder="Rennes, Lens, Brest…"
            autoFocus
            // 16 px : en dessous, Safari zoome à la mise au point et ne dézoome jamais.
            style={{ width: '100%', height: 50, borderRadius: 13, padding: '0 14px', fontSize: 16, background: 'var(--surf2)', border: '1px solid var(--line)', outline: 'none' }}
            autoCapitalize="words" autoCorrect="off" enterKeyHint="done"
          />
        </label>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <Bouton ton={domicile ? 'principal' : 'neutre'} large taille={46} onClick={() => setDomicile(true)}>À domicile</Bouton>
          <Bouton ton={!domicile ? 'principal' : 'neutre'} large taille={46} onClick={() => setDomicile(false)}>À l&apos;extérieur</Bouton>
        </div>

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '.8px', color: 'var(--ink2)', marginBottom: 6 }}>DATE</span>
          <input
            type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ width: '100%', height: 50, borderRadius: 13, padding: '0 14px', fontSize: 16, background: 'var(--surf2)', border: '1px solid var(--line)', outline: 'none' }}
          />
        </label>

        <Bouton
          ton="valide" large taille={52}
          disabled={!adversaire.trim()}
          style={adversaire.trim() ? null : { opacity: .45 }}
          onClick={() => onValider({ adversaire: adversaire.trim(), domicile, date })}
        >
          Commencer
        </Bouton>
      </div>
    </Feuille>
  );
}

const Phase = ({ actif, children, ...r }) => (
  <button style={{ height: 30, padding: '0 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5, background: actif ? 'var(--acc)' : 'transparent', color: actif ? 'var(--tokink)' : 'rgba(255,255,255,.75)' }} {...r}>{children}</button>
);

const BoutonIcone = ({ children, ...r }) => (
  <button style={{ width: 38, height: 38, flex: 'none', borderRadius: 11, background: 'var(--surf)', border: '1px solid var(--line)', color: 'var(--ink2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} {...r}>{children}</button>
);

const Consigne = ({ children, onAnnuler }) => (
  <div style={{ position: 'absolute', left: 12, right: 12, bottom: 'calc(env(safe-area-inset-bottom) + 118px)', zIndex: 30, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px 10px 15px', borderRadius: 14, background: 'var(--ink)', color: 'var(--bg)', boxShadow: 'var(--ombre)' }}>
    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{children}</span>
    <button onClick={onAnnuler} style={{ fontSize: 12.5, fontWeight: 700, opacity: .7 }}>Annuler</button>
  </div>
);

const nomDe = (parId, id) => parId.get(id)?.nom ?? '?';

function ActionMenu({ titre, detail, danger, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '13px 14px', marginBottom: 8, borderRadius: 14,
        background: 'var(--surf2)', border: `1px solid ${danger ? 'var(--bad)' : 'var(--line)'}`,
      }}
    >
      <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: danger ? 'var(--bad)' : 'var(--ink)' }}>{titre}</span>
      <span style={{ display: 'block', fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink2)', marginTop: 3 }}>{detail}</span>
    </button>
  );
}

const ETAT_LIBELLE = {
  probable: 'Brouillon',
  annoncee: 'Compo annoncée',
  'en-cours': 'Match en cours',
  jouee: 'Match terminé',
};

const etatCouleur = (e) => (e === 'en-cours' ? 'var(--bad)' : e === 'probable' ? 'var(--ink3)' : 'var(--acc2)');

const dateLisible = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

function desaccord(r, nousId, local) {
  if (!local || r?.domicile?.buts == null) return false;
  const nous = String(r.domicile.id) === String(nousId) ? r.domicile.buts : r.exterieur.buts;
  const eux = String(r.domicile.id) === String(nousId) ? r.exterieur.buts : r.domicile.buts;
  return nous !== local.nous || eux !== local.eux;
}

/**
 * Le score officiel, sur demande explicite.
 *
 * Volontairement PAS de sondage : le direct est le seul appelant autorisé à toucher la
 * réserve de quota, et un rafraîchissement automatique toutes les minutes la viderait
 * en une mi-temps. Un toucher, un appel — la question qu'on se pose vraiment, c'est
 * « est-ce que j'ai raté un but ? », et elle se pose deux fois par match.
 */
function ScoreOfficiel({ matchId, equipeId, scoreLocal }) {
  const [etat, setEtat] = useState('repos');
  const [donnees, setDonnees] = useState(null);

  if (!matchId) return null;

  async function chercher() {
    setEtat('charge');
    try {
      const r = await fetch(`/api/direct/${encodeURIComponent(matchId)}`, { signal: AbortSignal.timeout(8000) });
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      setDonnees(j?.valeur?.rencontre ?? null);
      setEtat(j?.valeur?.rencontre ? 'ok' : 'vide');
    } catch {
      // Sans réseau on ne dit pas « erreur », on dit ce qui est vrai : l'information
      // n'est pas joignable d'ici. Le reste de l'écran continue de marcher.
      setEtat('injoignable');
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', marginBottom: 6, borderRadius: 13, background: 'var(--surf2)', border: '1px solid var(--line)' }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--ink2)', lineHeight: 1.4 }}>
        {etat === 'ok' && donnees
          ? <>
              Score officiel <strong style={{ color: 'var(--ink)', fontFamily: MONO }}>{donnees.domicile?.buts ?? '–'}&nbsp;-&nbsp;{donnees.exterieur?.buts ?? '–'}</strong>
              {donnees.minute != null ? ` · ${donnees.minute}'` : ''}
              {/* Le rapprochement, pas la fusion : si les deux diffèrent, c'est qu'un
                  but est passé pendant qu'il regardait ailleurs. Lui dire suffit ; le
                  journal reste le sien, l'application ne le réécrit pas. */}
              {desaccord(donnees, equipeId, scoreLocal) && (
                <span style={{ display: 'block', color: 'var(--warn)', marginTop: 3 }}>
                  Ton journal dit {scoreLocal.nous}–{scoreLocal.eux}. Il te manque quelque chose.
                </span>
              )}
            </>
          : etat === 'vide' ? 'Le fournisseur n\u2019a rien sur ce match.'
          : etat === 'injoignable' ? 'Injoignable d\u2019ici — ton journal reste juste.'
          : 'Vérifier le score officiel'}
      </span>
      <Bouton ton="fantome" taille={38} disabled={etat === 'charge'} onClick={chercher} aria-label="Vérifier le score officiel">
        {etat === 'charge' ? '…' : <Rafraichir />}
      </Bouton>
    </div>
  );
}

// Le match qu'on est en train de poser passe avant celui du calendrier : c'est lui
// qu'on a sous les yeux, et le calendrier est souvent absent du plan gratuit.
function titreMatch(compo, prochain) {
  if (compo?.adversaire) return compo.domicile === false ? `${compo.adversaire} — AJA` : `AJA — ${compo.adversaire}`;
  if (prochain) return `${prochain.domicile?.nom ?? '?'} — ${prochain.exterieur?.nom ?? '?'}`;
  return 'Compo AJA';
}

function adversaireDe(m, nousId) {
  if (!m) return null;
  return String(m.domicile?.id) === String(nousId) ? m.exterieur?.nom : m.domicile?.nom;
}
